/**
 * Bundle Line Items Handler
 * Gère la synchronisation des quantités et suppressions pour les nested line items natifs de Shopify
 */

class BundleLineItemsHandler {
  constructor() {
    this.init();
  }

  init() {
    // Écouter les changements de quantité
    document.addEventListener('change', (e) => {
      const quantityInput = e.target.closest('quantity-input');
      if (quantityInput) {
        const cartItem = quantityInput.closest('.cart-item');
        if (cartItem && cartItem.classList.contains('cart-item--bundle-parent')) {
          this.handleParentQuantityChange(cartItem, quantityInput);
        }
      }
    });

    // Écouter les suppressions d'items
    document.addEventListener('click', (e) => {
      const removeButton = e.target.closest('cart-remove-button');
      if (removeButton) {
        const cartItem = removeButton.closest('.cart-item');
        if (cartItem && cartItem.classList.contains('cart-item--bundle-parent')) {
          this.handleParentRemoval(cartItem);
        }
      }
    });

    // Initialiser l'affichage des bundles
    this.updateBundleDisplay();
  }

  /**
   * Récupère la clé (key) d'un item parent pour trouver ses enfants
   */
  getItemKey(cartItem) {
    return cartItem.dataset.itemKey || cartItem.getAttribute('data-item-key');
  }

  /**
   * Trouve tous les items enfants d'un parent via parent_relationship
   */
  async findBundleChildren(parentKey) {
    // Les enfants sont identifiés par la classe cart-item--nested
    // et leur parent_id correspond à l'ID du parent
    return Array.from(document.querySelectorAll('.cart-item--nested'));
  }

  /**
   * Gère le changement de quantité du parent
   * Shopify NE synchronise PAS automatiquement les quantités des enfants
   * On doit le faire manuellement avec une synchronisation proportionnelle
   */
  async handleParentQuantityChange(parentItem, quantityInput) {
    const parentKey = this.getItemKey(parentItem);
    if (!parentKey) return;

    const newQuantity = parseInt(quantityInput.querySelector('input').value);
    console.log('🔄 Changement quantité parent:', parentKey, 'nouvelle quantité:', newQuantity);
    
    // Récupérer le panier pour trouver les enfants
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      console.log('📦 Panier complet:', cart);
      
      // Trouver l'item parent par sa clé
      const parentItemData = cart.items.find(item => item.key === parentKey);
      if (!parentItemData) {
        console.log('❌ Parent non trouvé dans le panier');
        return;
      }
      
      console.log('👨 Parent trouvé:', parentItemData);
      console.log('📊 Ancienne quantité parent:', parentItemData.quantity);
      console.log('📊 Nouvelle quantité parent:', newQuantity);
      
      // Calculer le ratio de changement
      const oldQuantity = parentItemData.quantity;
      const ratio = newQuantity / oldQuantity;
      
      console.log('📈 Ratio de changement:', ratio);
      
      // Trouver tous les enfants
      // Les enfants ont un parent_line_item_id qui correspond à l'id du parent
      const children = cart.items.filter(item => {
        // Vérifier si cet item a un parent_line_item_id correspondant
        const hasParentId = item.parent_line_item_id && 
                           item.parent_line_item_id === parentItemData.id;
        
        if (hasParentId) {
          console.log('👶 Enfant trouvé:', item);
        }
        
        return hasParentId;
      });
      
      console.log(`👶 Total enfants trouvés: ${children.length}`);
      
      // Mettre à jour la quantité de chaque enfant proportionnellement
      for (const child of children) {
        const oldChildQuantity = child.quantity;
        const newChildQuantity = Math.round(oldChildQuantity * ratio);
        
        console.log(`🔄 Enfant ${child.key}:`);
        console.log(`   - Ancienne quantité: ${oldChildQuantity}`);
        console.log(`   - Ratio appliqué: ${ratio}`);
        console.log(`   - Nouvelle quantité: ${newChildQuantity}`);
        
        await this.updateItemQuantity(child.key, newChildQuantity);
        console.log(`✅ Quantité enfant ${child.key} mise à jour: ${oldChildQuantity} → ${newChildQuantity}`);
      }
      
      // Rafraîchir l'affichage du panier
      if (children.length > 0) {
        // Petit délai pour que les changements soient pris en compte
        setTimeout(() => {
          if (window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent('cart:refresh'));
          }
          // Alternativement, rafraîchir via le cart drawer/notification
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
            fetch('/cart.js')
              .then(res => res.json())
              .then(data => cartDrawer.renderContents(data));
          }
        }, 300);
      }
    } catch (error) {
      console.error('❌ Erreur synchronisation quantités:', error);
    }
  }

  /**
   * Gère la suppression du parent
   * Note: Shopify supprime automatiquement les enfants quand le parent est supprimé
   */
  async handleParentRemoval(parentItem) {
    const parentKey = this.getItemKey(parentItem);
    if (!parentKey) return;

    console.log('🗑️ Suppression du parent:', parentKey);
    
    // Note: Shopify gère automatiquement la suppression des enfants
    // quand le parent est supprimé
  }

  /**
   * Met à jour la quantité d'un item
   */
  async updateItemQuantity(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update quantity');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating item quantity:', error);
    }
  }

  /**
   * Supprime un item du panier
   */
  async removeItem(key) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  /**
   * Met à jour l'affichage des bundles
   */
  updateBundleDisplay() {
    // Masquer les prix des items enfants (ils sont déjà inclus dans le parent)
    document.querySelectorAll('.cart-item--nested .cart-item__price-wrapper').forEach(priceWrapper => {
      priceWrapper.style.display = 'none';
    });

    // Ajouter une indication visuelle aux bundles
    document.querySelectorAll('.cart-item--bundle-parent').forEach(parent => {
      const bundleId = this.getBundleId(parent);
      if (bundleId) {
        const children = this.findBundleChildren(bundleId);
        if (children.length > 0) {
          parent.setAttribute('data-bundle-items', children.length);
        }
      }
    });
  }
}

// Initialiser le gestionnaire au chargement du DOM
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BundleLineItemsHandler();
  });
} else {
  new BundleLineItemsHandler();
}

// Réinitialiser après un rafraîchissement du panier
document.addEventListener('cart:refresh', () => {
  new BundleLineItemsHandler();
});

